import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { adminApi, type CreateAdminProviderInput, type ProviderApprovalStatus } from "../../../lib/api";
import { useAuth } from "../../../lib/auth";
import { useAppPreferences } from "../../../lib/preferences";
import { errorMessage, LoadState, PageHeader, Section, StatusChip, useFormatters, useLoad } from "../ui";

const STATUS_FILTERS: Array<{ id: "" | ProviderApprovalStatus; label: string }> = [
  { id: "", label: "All" },
  { id: "approved", label: "Approved" },
  { id: "pending", label: "Pending" },
  { id: "suspended", label: "Suspended" },
  { id: "rejected", label: "Rejected" },
];

export function ProvidersPage() {
  const { t } = useAppPreferences();
  const { user } = useAuth();
  const { dateOnly } = useFormatters();
  const [params, setParams] = useSearchParams();
  const status = (params.get("status") ?? "") as "" | ProviderApprovalStatus;
  const query = params.get("q") ?? "";
  const providers = useLoad(() => adminApi.listProviders().then((response) => response.providers), []);
  const needle = query.trim().toLowerCase();
  const visible = (providers.data ?? []).filter(
    (provider) =>
      (!status || provider.approval_status === status) &&
      (!needle ||
        [provider.business_name, provider.city, provider.contact_email, provider.contact_name ?? "", provider.contact_phone ?? ""]
          .join(" ")
          .toLowerCase()
          .includes(needle)),
  );
  const update = (next: Record<string, string>) => {
    const merged = { status, q: query, ...next };
    setParams(Object.fromEntries(Object.entries(merged).filter(([, value]) => value)));
  };

  return (
    <div className="console-page">
      <PageHeader
        actions={user?.role === "admin" ? <Link className="ops-button" data-variant="primary" to="/admin/providers/new">{t("Add provider")}</Link> : null}
        kicker={t("Records")}
        title={t("Providers")}
        description={t("Every business on gaamed, with its venues, team and settlement.")}
      />
      <div className="console-toolbar">
        <input
          aria-label={t("Filter providers")}
          className="ops-input console-filter-input"
          onChange={(event) => update({ q: event.target.value })}
          placeholder={t("Filter by name, city, email or phone")}
          value={query}
        />
        <div className="console-pills">
          {STATUS_FILTERS.map((item) => (
            <button aria-pressed={status === item.id} className="ops-filter-pill" data-active={status === item.id} key={item.id || "all"} onClick={() => update({ status: item.id })} type="button">
              {t(item.label)}
            </button>
          ))}
        </div>
      </div>
      <LoadState
        empty={t("No providers match.")}
        error={providers.error}
        isEmpty={visible.length === 0}
        loading={providers.loading}
        onRetry={() => void providers.reload()}
      >
        <div className="ops-surface console-table-wrap">
          <table className="console-table">
            <thead>
              <tr>
                <th scope="col">{t("Provider")}</th>
                <th scope="col">{t("Status")}</th>
                <th scope="col">{t("Owner")}</th>
                <th scope="col">{t("City")}</th>
                <th scope="col">{t("Joined")}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((provider) => (
                <tr key={provider.id}>
                  <td><Link className="console-link" to={`/admin/providers/${provider.id}`}>{provider.business_name}</Link></td>
                  <td><StatusChip status={provider.approval_status} /></td>
                  <td>
                    <span className="block">{provider.contact_name ?? "—"}</span>
                    <span className="console-muted console-truncate">{provider.contact_email}</span>
                  </td>
                  <td>{provider.city}</td>
                  <td>{dateOnly(provider.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </LoadState>
    </div>
  );
}

export function ProviderCreatePage() {
  const { t } = useAppPreferences();
  const navigate = useNavigate();
  const [form, setForm] = useState<CreateAdminProviderInput>({
    business_name: "",
    city: "",
    contact_name: "",
    contact_phone: "",
    email: "",
    application_note: "",
    send_setup_email: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const field = (key: keyof CreateAdminProviderInput) => (value: string) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const created = await adminApi.createProvider(form);
      const notice =
        created.setup_email.code === "setup_email_sent"
          ? t("{name} added. A 7-day password setup link was sent to {email}.", { name: created.business_name, email: created.contact_email })
          : created.setup_email.code === "setup_email_deferred"
            ? t("{name} added. Prepare the workspace, then send the owner a setup link.", { name: created.business_name })
            : t("{name} was created, but the setup email failed. Use Send setup link on the record to retry.", { name: created.business_name });
      navigate(`/admin/providers/${created.id}`, { state: { notice } });
    } catch (submitError) {
      setError(errorMessage(submitError, t("The provider was not created.")));
      setSaving(false);
    }
  };

  const inputs: Array<[keyof CreateAdminProviderInput, string, string?]> = [
    ["business_name", "Business name"],
    ["contact_name", "Owner's name"],
    ["email", "Owner's email", "email"],
    ["contact_phone", "Phone"],
    ["city", "City"],
  ];

  return (
    <div className="console-page">
      <PageHeader kicker={<Link className="console-link" to="/admin/providers">{t("Providers")}</Link>} title={t("Add a provider")} />
      <Section description={t("Prepare the workspace before inviting the owner. Admins never see or set their password.")} title={t("Business details")}>
        <form className="console-form" onSubmit={(event) => void submit(event)}>
          {inputs.map(([key, label, type]) => (
            <label className="ops-field" key={key}>
              <span className="ops-label">{t(label)}</span>
              <input
                className="ops-input"
                onChange={(event) => field(key)(event.target.value)}
                required={key !== "contact_phone"}
                type={type ?? "text"}
                value={String(form[key] ?? "")}
              />
            </label>
          ))}
          <label className="ops-field console-form-wide">
            <span className="ops-label">{t("Internal application note")}</span>
            <textarea className="ops-textarea" onChange={(event) => field("application_note")(event.target.value)} rows={3} value={form.application_note ?? ""} />
          </label>
          <label className="console-checkbox console-form-wide">
            <input
              checked={form.send_setup_email ?? false}
              onChange={(event) => setForm((current) => ({ ...current, send_setup_email: event.target.checked }))}
              type="checkbox"
            />
            {t("Email the owner a password setup link now")}
          </label>
          {error ? <p className="console-error console-form-wide" role="alert">{error}</p> : null}
          <div className="console-button-row console-form-wide">
            <button className="ops-button" data-variant="primary" disabled={saving} type="submit">{saving ? t("Saving...") : t("Create provider")}</button>
            <Link className="ops-button" data-variant="secondary" to="/admin/providers">{t("Cancel")}</Link>
          </div>
        </form>
      </Section>
    </div>
  );
}
